import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
  fetchAvailableCities,
  fetchCities,
  updateCityAvailability,
} from "../../redux/slices/citySlice";
import { LocationPanelItem } from "../../components/admin/LocationsPanel/LocationPanelItem";
import { Plus } from "lucide-react";
import { City } from "../../types/city.types";
import { CityDetailsModal } from "../../components/admin/LocationsPanel/CityDetailsModal";

export const LocationsPanel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { activeCities, availableCities, selectedCity } = useSelector(
    (state: RootState) => state.city
  );

  const [citySearchQuery, setCitySearchQuery] = useState("");
  const [isCityPickerOpen, setIsCityPickerOpen] = useState(false);
  const [activeCity, setActiveCity] = useState<City | null>(null);

  const cityPickerRef = useRef<HTMLDivElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!activeCities.length) {
      dispatch(fetchAvailableCities() as any);
    }
  }, [activeCities.length, dispatch]);

  useEffect(() => {
    if (isCityPickerOpen) {
      dispatch(
        fetchCities({
          search: citySearchQuery,
          lat: 0,
          lng: 0,
          selectedCity: selectedCity,
        }) as any
      );
    }
  }, [isCityPickerOpen, citySearchQuery, dispatch]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      if (
        isCityPickerOpen &&
        cityPickerRef.current &&
        !cityPickerRef.current.contains(target) &&
        addButtonRef.current &&
        !addButtonRef.current.contains(target)
      ) {
        setIsCityPickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCityPickerOpen]);

  const handleAddCity = (city: City) => {
    setActiveCity(city);
    setIsCityPickerOpen(false);
    setCitySearchQuery("");
  };

  const handleToggleAvailability = (city: City) => {
    dispatch(
      updateCityAvailability({
        cityID: city._id,
        isAvailable: !city.isAvailable,
      }) as any
    );
    setActiveCity(null);
  };

  return (
    <div className="p-6 relative">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">
          {t("Active Locations")}
          <span className="ml-2 text-sm text-gray-500">
            ({activeCities.length})
          </span>
        </h1>

        <button
          ref={addButtonRef}
          onClick={() => setIsCityPickerOpen((prev) => !prev)}
          className="h-10 w-10 flex items-center justify-center rounded-lg shadow bg-popupLight dark:bg-popupDark hover:bg-gray-200 dark:hover:bg-gray-700 transition"
        >
          <Plus size={18} />
        </button>
      </div>

      <div className="flex flex-col gap-3">
        {activeCities.map((city) => (
          <LocationPanelItem
            key={city._id}
            city={city}
            onSelect={(city) => handleAddCity(city)}
          />
        ))}

        {!activeCities.length && (
          <div className="text-sm text-gray-500 text-center py-10">
            {t("No active locations found")}
          </div>
        )}
      </div>

      {isCityPickerOpen && (
        <div
          ref={cityPickerRef}
          className="absolute right-6 top-20 w-72 bg-popupLight dark:bg-popupDark shadow-2xl rounded-md p-3 z-50"
        >
          <h3 className="text-sm mb-2 font-semibold dark:text-primaryTextDark text-primaryText">
            {t("Select City")}
          </h3>

          <input
            type="text"
            placeholder={`${t("Search city")}...`}
            value={citySearchQuery}
            autoFocus
            onChange={(e) => setCitySearchQuery(e.target.value)}
            className="w-full px-3 py-2 rounded bg-gray-200 dark:bg-gray-700 mb-3 text-sm outline-none text-primaryText dark:text-primaryTextDark"
          />

          <div className="max-h-48 overflow-y-auto custom-scroll">
            {availableCities.map((city) => {
              const isActive = activeCities.some((c) => c._id === city._id);

              return (
                <button
                  key={city._id}
                  onClick={() => handleAddCity(city)}
                  className="w-full text-left px-2 py-1 text-sm rounded hover:bg-gray-200 dark:hover:bg-gray-700 flex justify-between items-center text-primaryText dark:text-primaryTextDark"
                >
                  {city.city}, {city.country}
                  {isActive && (
                    <span className="text-green-600 dark:text-green-400 text-xs">
                      ✓
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {activeCity && (
        <CityDetailsModal
          city={activeCity}
          onClose={() => setActiveCity(null)}
          onToggleAvailability={handleToggleAvailability}
        />
      )}
    </div>
  );
};
